import axios, { type AxiosInstance } from 'axios'
import type {
  ChangePasswordRequest,
  CreateSchemaVersion,
  CreateUserRequest,
  DataSet,
  DataSetIngestResult,
  DataSetListResponse,
  DataSetLookupRequest,
  DataSetLookupResult,
  DataSetQuery,
  IngestEnvelope,
  JsonSchemaBody,
  LoginRequest,
  ManualIngestRequest,
  PatchMeRequest,
  PatchUserRequest,
  RecoverRequest,
  ResetPasswordRequest,
  SchemaSummary,
  SchemaVersion,
  Tablet,
  TabletApprovalResponse,
  TabletPatch,
  TabletRegisterRequest,
  TabletSelfStatus,
  TemporaryPasswordResult,
  User,
} from '@collector/shared'
import { installErrorInterceptor } from './interceptors/error'
import { installRequestIdInterceptor } from './interceptors/request-id'

export interface HmacConfig {
  tabletUid: string
  secret: string
}

export interface CreateClientOptions {
  baseURL: string
  /** Include cookies for browser session-cookie auth. */
  withCredentials?: boolean
  /**
   * Optional callback invoked with the underlying axios instance after the
   * standard interceptors are installed. Use this from the Node-side entry
   * point to attach the HMAC signing interceptor (tablet-sim, e2e tests).
   * Browser code never sets this.
   */
  configureAxios?: (instance: AxiosInstance) => void
  /** Optional pre-created axios instance to wrap (tests). */
  instance?: AxiosInstance
  /** Request timeout in ms. Defaults to 15s. */
  timeoutMs?: number
}

export interface ApiClient {
  readonly axios: AxiosInstance
  auth: AuthApi
  users: UsersApi
  tablets: TabletsApi
  schemas: SchemasApi
  dataSets: DataSetsApi
  health: HealthApi
}

export function createApiClient(opts: CreateClientOptions): ApiClient {
  const instance =
    opts.instance ??
    axios.create({
      baseURL: opts.baseURL,
      withCredentials: opts.withCredentials ?? false,
      timeout: opts.timeoutMs ?? 15_000,
      headers: { Accept: 'application/json' },
    })
  installRequestIdInterceptor(instance)
  installErrorInterceptor(instance)
  opts.configureAxios?.(instance)
  return {
    axios: instance,
    auth: new AuthApi(instance),
    users: new UsersApi(instance),
    tablets: new TabletsApi(instance),
    schemas: new SchemasApi(instance),
    dataSets: new DataSetsApi(instance),
    health: new HealthApi(instance),
  }
}

class AuthApi {
  constructor(private readonly http: AxiosInstance) {}
  async login(body: LoginRequest): Promise<User> {
    const { data } = await this.http.post<User>('/auth/login', body)
    return data
  }
  async logout(): Promise<void> {
    await this.http.post('/auth/logout')
  }
  async me(): Promise<User> {
    const { data } = await this.http.get<User>('/auth/me')
    return data
  }
  /** Self-service profile patch for the authenticated user. */
  async patchMe(body: PatchMeRequest): Promise<User> {
    const { data } = await this.http.patch<User>('/auth/me', body)
    return data
  }
  async recover(body: RecoverRequest): Promise<void> {
    await this.http.post('/auth/recover', body)
  }
  async resetWithToken(token: string, body: ResetPasswordRequest): Promise<void> {
    await this.http.post(`/auth/recover/${encodeURIComponent(token)}`, body)
  }
  /** Self-service: change own password (verifies the current one). */
  async changePassword(body: ChangePasswordRequest): Promise<void> {
    await this.http.post('/auth/me/password', body)
  }
}

class UsersApi {
  constructor(private readonly http: AxiosInstance) {}
  async list(): Promise<User[]> {
    const { data } = await this.http.get<User[]>('/users')
    return data
  }
  async create(body: CreateUserRequest): Promise<TemporaryPasswordResult> {
    const { data } = await this.http.post<TemporaryPasswordResult>('/users', body)
    return data
  }
  async patch(id: string, body: PatchUserRequest): Promise<User> {
    const { data } = await this.http.patch<User>(`/users/${id}`, body)
    return data
  }
  /** Admin-only: regenerate a random password and force a change on next login. */
  async resetPassword(id: string): Promise<TemporaryPasswordResult> {
    const { data } = await this.http.post<TemporaryPasswordResult>(`/users/${id}/reset-password`)
    return data
  }
  async delete(id: string): Promise<void> {
    await this.http.delete(`/users/${id}`)
  }
}

class TabletsApi {
  constructor(private readonly http: AxiosInstance) {}
  // Tablet-facing (public-ish)
  async register(body: TabletRegisterRequest): Promise<TabletSelfStatus> {
    const { data } = await this.http.post<TabletSelfStatus>('/tablets/register', body)
    return data
  }
  async self(tabletUid: string): Promise<TabletSelfStatus> {
    const { data } = await this.http.get<TabletSelfStatus>('/tablets/self', {
      headers: { 'X-Tablet-Uid': tabletUid },
    })
    return data
  }
  async ingest(envelope: IngestEnvelope): Promise<{ id: string }> {
    const { data } = await this.http.post<{ id: string }>('/tablets/ingest', envelope)
    return data
  }
  // Admin UI
  async list(status?: 'pending' | 'approved'): Promise<Tablet[]> {
    const { data } = await this.http.get<Tablet[]>('/tablets', {
      params: status ? { status } : undefined,
    })
    return data
  }
  async approve(id: string, name?: string): Promise<TabletApprovalResponse> {
    const { data } = await this.http.post<TabletApprovalResponse>(
      `/tablets/${id}/approve`,
      name ? { name } : {},
    )
    return data
  }
  async patch(id: string, body: TabletPatch): Promise<Tablet> {
    const { data } = await this.http.patch<Tablet>(`/tablets/${id}`, body)
    return data
  }
  async revoke(id: string): Promise<void> {
    await this.http.delete(`/tablets/${id}`)
  }
}

class SchemasApi {
  constructor(private readonly http: AxiosInstance) {}
  async list(): Promise<SchemaSummary[]> {
    const { data } = await this.http.get<SchemaSummary[]>('/schemas')
    return data
  }
  async versions(name: string): Promise<SchemaVersion[]> {
    const { data } = await this.http.get<SchemaVersion[]>(`/schemas/${encodeURIComponent(name)}`)
    return data
  }
  async get(name: string, version: number): Promise<SchemaVersion> {
    const { data } = await this.http.get<SchemaVersion>(
      `/schemas/${encodeURIComponent(name)}/${version}`,
    )
    return data
  }
  async create(body: CreateSchemaVersion): Promise<SchemaVersion> {
    const { data } = await this.http.post<SchemaVersion>('/schemas', body)
    return data
  }
  async deprecate(name: string, version: number, deprecated: boolean): Promise<SchemaVersion> {
    const { data } = await this.http.patch<SchemaVersion>(
      `/schemas/${encodeURIComponent(name)}/${version}`,
      { deprecated },
    )
    return data
  }
  async fetchSchemaBody(name: string, version: number): Promise<JsonSchemaBody> {
    const v = await this.get(name, version)
    return v.jsonSchema
  }
}

class DataSetsApi {
  constructor(private readonly http: AxiosInstance) {}
  async list(query: Partial<DataSetQuery> = {}): Promise<DataSetListResponse> {
    const { data } = await this.http.get<DataSetListResponse>('/data-sets', { params: query })
    return data
  }
  async tabletNames(): Promise<string[]> {
    const { data } = await this.http.get<string[]>('/data-sets/tablet-names')
    return data
  }
  async get(id: string): Promise<DataSet> {
    const { data } = await this.http.get<DataSet>(`/data-sets/${id}`)
    return data
  }
  async create(body: ManualIngestRequest): Promise<DataSetIngestResult> {
    const { data } = await this.http.post<DataSetIngestResult>('/data-sets', body)
    return data
  }
  async lookup(body: DataSetLookupRequest): Promise<DataSetLookupResult> {
    const { data } = await this.http.post<DataSetLookupResult>('/data-sets/lookup', body)
    return data
  }
  async delete(id: string): Promise<void> {
    await this.http.delete(`/data-sets/${id}`)
  }
}

class HealthApi {
  constructor(private readonly http: AxiosInstance) {}
  async check(): Promise<{ status: 'ok'; version: string }> {
    const { data } = await this.http.get<{ status: 'ok'; version: string }>('/healthz')
    return data
  }
}
